import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Link,
  Stack,
  Text,
  useColorModeValue,
  HStack,
  Icon,
  VStack,
  Badge,
  Circle,
  Center,
  Divider,
  Avatar,
  SlideFade,
  useDisclosure,
} from "@chakra-ui/react";
import { 
  FaDollarSign, 
  FaClipboardList, 
  FaPercent,
  FaCalendarAlt,
  FaSyncAlt,
  FaEnvelope,
  FaVideo,
  FaFile,
  FaPhone,
  FaWhatsapp,
  FaFileContract,
  FaCreditCard,
  FaBuilding,
  FaClock,
  FaTag,
} from "react-icons/fa";
import {
  MdTrackChanges,
  MdInbox,
  MdEqualizer,
  MdBusiness,
  MdPerson,
  MdEvent,
  MdRocketLaunch,
} from "react-icons/md";
import { RiLightbulbFlashLine } from "react-icons/ri";
// Import the Lysto logo SVG
import { ReactComponent as LystoLogo } from "assets/img/lysto-logo-round.svg";
// Import the WaitlistModal component
import WaitlistModal from "./WaitlistModal";

const LandingHero = () => {
  // Theme colors - match with header
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.300");
  const primaryButtonBg = useColorModeValue("blue.500", "blue.300");
  const primaryButtonColor = useColorModeValue("white", "gray.800");
  const secondaryButtonBg = useColorModeValue("white", "gray.700");
  const secondaryButtonColor = useColorModeValue("gray.700", "gray.100");
  const secondaryButtonBorder = useColorModeValue("gray.300", "gray.600");
  const ycColor = "#F26522"; // Y Combinator color
  
  // Modal disclosure
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Sidebar and app colors
  const sidebarBg = useColorModeValue("white", "gray.800");
  const sidebarTextColor = useColorModeValue("gray.800", "white");
  const sidebarBorderColor = useColorModeValue("gray.200", "gray.700");
  const activeLinkBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const activeLinkColor = useColorModeValue("blue.500", "blue.200");
  const cardBgColor = useColorModeValue("white", "gray.800");
  const cardBorderColor = useColorModeValue("gray.200", "gray.700");
  const highlightBg = useColorModeValue("blue.50", "blue.900");
  const browserAddressBg = useColorModeValue("gray.100", "gray.700");

  // Animation states
  const [currentStage, setCurrentStage] = useState(0);
  const [dealData, setDealData] = useState({});
  const [visibleActivities, setVisibleActivities] = useState([]);

  // Navigation links from LandingHeader.jsx with additional links
  const navLinks = [
    { 
      name: "Inbox", 
      sectionId: "inbox-section", 
      icon: MdInbox,
      isActive: false
    },
    { 
      name: "Meetings", 
      sectionId: "meetings-section", 
      icon: MdEvent,
      isActive: false 
    },
    { 
      name: "Deals", 
      sectionId: "deal-section", 
      icon: MdTrackChanges,
      isActive: true
    },
    { 
      name: "Contacts", 
      sectionId: "contacts-section", 
      icon: MdPerson,
      isActive: false
    },
    { 
      name: "Accounts", 
      sectionId: "accounts-section", 
      icon: MdBusiness,
      isActive: false 
    },
    { 
      name: "Spark", 
      sectionId: "spark-section", 
      icon: RiLightbulbFlashLine,
      isActive: false
    },
    { 
      name: "Reports", 
      sectionId: "reports-section", 
      icon: MdEqualizer,
      isActive: false 
    },
  ];

  // Define deal stages with progressive information
  const dealStages = [
    {
      name: "Lumon Industries Deal",
      amount: "150,000",
      stage: "Qualification",
      probability: 65,
      closeDate: "2023-10-15",
      lastActivityDate: "2023-08-15",
      owner: "Ricky Roma",
      accountName: "Lumon Industries",
      accountId: 12345,
      summary: "Lumon Industries is exploring a partnership for biotechnology solutions. Initial meeting completed with positive feedback. They're interested in our enterprise offering.",
      shortSummary: "Biotech partnership in qualification phase. Initial interest confirmed.",
      updatedAt: "Just now",
      dateRange: "June 5th, 2023 - August 15th, 2023",
      activities: [
        {
          id: "act12350",
          title: "Partnership Inquiry",
          date: "Just now",
          sender: "Kier Eagan",
          content: "Hi team, I'm interested in exploring a partnership with your biotech solutions. Lumon Industries has been at the forefront of biotechnology innovation for years, and we believe your products would integrate well with our platform.",
          source: "Gmail",
          icon: FaEnvelope,
          iconColor: "red",
          isNew: true
        }
      ]
    },
    {
      name: "Lumon Industries Deal",
      amount: "150,000",
      stage: "Discovery",
      probability: 70,
      closeDate: "2023-10-15",
      lastActivityDate: "2023-08-15",
      owner: "Ricky Roma",
      accountName: "Lumon Industries",
      accountId: 12345,
      summary: "Lumon Industries is exploring a partnership for biotechnology solutions. Initial demo completed with positive feedback. Technical team has begun evaluating our API documentation.",
      shortSummary: "Biotech partnership advancing to discovery phase. Technical evaluation in progress.",
      updatedAt: "Just now",
      dateRange: "June 5th, 2023 - August 15th, 2023",
      activities: [
        {
          id: "act12349",
          title: "Integration Demo",
          date: "Just now",
          sender: "Helena Eagan",
          content: "Recording summary: Presented technical demo to Lumon's engineering team. They were impressed with the API capabilities but had questions about data migration tools. Action items: Share migration documentation and schedule follow-up with their CTO.",
          source: "Zoom",
          icon: FaVideo,
          iconColor: "purple",
          isNew: true
        },
        {
          id: "act12350",
          title: "Partnership Inquiry",
          date: "2 days ago",
          sender: "Kier Eagan",
          content: "Hi team, I'm interested in exploring a partnership with your biotech solutions. Lumon Industries has been at the forefront of biotechnology innovation for years, and we believe your products would integrate well with our platform.",
          source: "Gmail",
          icon: FaEnvelope,
          iconColor: "red",
          isNew: false
        }
      ]
    },
    {
      name: "Lumon Industries Deal",
      amount: "175,000",
      stage: "Proposal",
      probability: 80,
      closeDate: "2023-10-15",
      lastActivityDate: "2023-08-15",
      owner: "Ricky Roma",
      accountName: "Lumon Industries",
      accountId: 12345,
      summary: "Lumon Industries partnership progressing well. Budget discussion has increased potential deal size to $175K. Security requirements document received and being reviewed by our team.",
      shortSummary: "Budget approved. Deal size increased. Security requirements under review.",
      updatedAt: "Just now",
      dateRange: "June 5th, 2023 - August 15th, 2023",
      activities: [
        {
          id: "act12347",
          title: "Security Requirements",
          date: "Just now",
          sender: "Dario R.",
          content: "The Lumon Industries security team has shared their compliance requirements document. They're requesting information about our data handling practices, encryption protocols, and SOC 2 certification.",
          source: "Google Drive",
          icon: FaFile,
          iconColor: "blue",
          isNew: true
        },
        {
          id: "act12348",
          title: "Budget Discussion",
          date: "2 days ago",
          sender: "Leonora Eagan",
          content: "Call summary: Discussed pricing structure with Lumon's finance team. They have a budget of $150-200K for this partnership and prefer quarterly billing. They're requesting a detailed breakdown of implementation costs.",
          source: "Fireflies",
          icon: FaPhone,
          iconColor: "teal",
          isNew: false
        },
        {
          id: "act12349",
          title: "Integration Demo",
          date: "5 days ago",
          sender: "Helena Eagan",
          content: "Recording summary: Presented technical demo to Lumon's engineering team. They were impressed with the API capabilities but had questions about data migration tools. Action items: Share migration documentation and schedule follow-up with their CTO.",
          source: "Zoom",
          icon: FaVideo,
          iconColor: "purple",
          isNew: false
        }
      ]
    },
    {
      name: "Lumon Industries Deal",
      amount: "175,000",
      stage: "Negotiation",
      probability: 85,
      closeDate: "2023-10-15",
      lastActivityDate: "2023-08-15",
      owner: "Ricky Roma",
      accountName: "Lumon Industries",
      accountId: 12345,
      summary: "Lumon Industries is exploring a partnership for biotechnology solutions. Legal review in progress with expected completion next week. Implementation timeline confirmed for Q4 rollout. Customer is requesting additional security documentation and compliance information before final signature.",
      shortSummary: "Biotech partnership in legal review. Q4 implementation planned.",
      updatedAt: "Just now",
      dateRange: "June 5th, 2023 - August 15th, 2023",
      activities: [
        {
          id: "act12346",
          title: "Follow-up Message",
          date: "Just now",
          sender: "Phillip Eagan",
          content: "Thanks for your quick response to our partnership inquiry. Our executive team is excited about the potential collaboration and would like to fast-track the process.",
          source: "WhatsApp",
          icon: FaWhatsapp,
          iconColor: "green",
          isNew: true
        },
        {
          id: "act12347",
          title: "Security Requirements",
          date: "2 days ago",
          sender: "Dario R.",
          content: "The Lumon Industries security team has shared their compliance requirements document. They're requesting information about our data handling practices, encryption protocols, and SOC 2 certification.",
          source: "Google Drive",
          icon: FaFile,
          iconColor: "blue",
          isNew: false
        },
        {
          id: "act12348",
          title: "Budget Discussion",
          date: "4 days ago",
          sender: "Leonora Eagan",
          content: "Call summary: Discussed pricing structure with Lumon's finance team. They have a budget of $150-200K for this partnership and prefer quarterly billing. They're requesting a detailed breakdown of implementation costs.",
          source: "Fireflies",
          icon: FaPhone,
          iconColor: "teal",
          isNew: false
        }
      ]
    },
    {
      name: "Lumon Industries Deal",
      amount: "175,000",
      stage: "Contract",
      probability: 90,
      closeDate: "2023-10-15",
      lastActivityDate: "2023-08-15",
      owner: "Ricky Roma",
      accountName: "Lumon Industries",
      accountId: 12345,
      summary: "Lumon Industries contract in final stage. All terms agreed upon with implementation schedule finalized. Executive meeting scheduled for Tuesday to discuss partnership rollout and next steps.",
      shortSummary: "Contract in final review. Executive meeting scheduled.",
      updatedAt: "Just now",
      dateRange: "June 5th, 2023 - August 15th, 2023",
      activities: [
        {
          id: "act12345",
          title: "Executive Meeting",
          date: "Just now",
          sender: "Myrtle Eagan",
          content: "Initial discussion with Lumon Industries executive team. Attendees: CEO, CTO (Lumon), Partnership lead, Product team (us). Location: Virtual. Agenda: Introduction, partnership scope discussion, technical integration possibilities.",
          source: "Google Calendar",
          icon: FaCalendarAlt,
          iconColor: "purple",
          isNew: true
        },
        {
          id: "act12345b",
          title: "Contract Draft",
          date: "1 day ago",
          sender: "Carol D.",
          content: "The final contract has been drafted and shared with Lumon's legal team. All negotiated terms are included with implementation timeline and payment schedule.",
          source: "Google Drive",
          icon: FaFileContract,
          iconColor: "teal",
          isNew: false
        },
        {
          id: "act12346",
          title: "Follow-up Message",
          date: "3 days ago",
          sender: "Phillip Eagan",
          content: "Thanks for your quick response to our partnership inquiry. Our executive team is excited about the potential collaboration and would like to fast-track the process.",
          source: "WhatsApp",
          icon: FaWhatsapp,
          iconColor: "green",
          isNew: false
        }
      ]
    },
    {
      name: "Lumon Industries Deal",
      amount: "175,000",
      stage: "Closed Won",
      probability: 100,
      closeDate: "2023-10-15",
      lastActivityDate: "2023-08-15",
      owner: "Ricky Roma",
      accountName: "Lumon Industries",
      accountId: 12345,
      summary: "Lumon Industries partnership is now closed! Contract signed and initial payment processed. Implementation scheduled to begin next week. Customer success team has been assigned for onboarding.",
      shortSummary: "Deal closed! Implementation starting next week.",
      updatedAt: "Just now",
      dateRange: "June 5th, 2023 - August 15th, 2023",
      activities: [
        {
          id: "act12344",
          title: "Payment Processed",
          date: "Just now",
          sender: "Peter K.",
          content: "Initial payment from Lumon Industries has been processed. First quarter payment of $45,000 received. Implementation team has been notified to begin onboarding process.",
          source: "Stripe",
          icon: FaCreditCard,
          iconColor: "purple",
          isNew: true
        },
        {
          id: "act12345",
          title: "Contract Signed",
          date: "1 day ago",
          sender: "Imogene Eagan",
          content: "The contract has been signed by both parties. All terms are finalized with implementation to begin next week. Customer success team will handle the onboarding process.",
          source: "PandaDoc",
          icon: FaFileContract,
          iconColor: "teal",
          isNew: false
        },
        {
          id: "act12345b",
          title: "Executive Meeting",
          date: "3 days ago",
          sender: "Myrtle Eagan",
          content: "Initial discussion with Lumon Industries executive team. Attendees: CEO, CTO (Lumon), Partnership lead, Product team (us). Location: Virtual. Agenda: Introduction, partnership scope discussion, technical integration possibilities.",
          source: "Google Calendar",
          icon: FaCalendarAlt,
          iconColor: "purple",
          isNew: false
        }
      ]
    }
  ];

  // Function to scroll to sections
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      // Get header height (estimated at 70px if we can't determine it)
      const headerHeight = 70;
      const sectionPosition = section.getBoundingClientRect().top + window.pageYOffset;
      
      // Scroll to position where the bottom of header aligns with section top
      window.scrollTo({ 
        top: sectionPosition - headerHeight, 
        behavior: 'smooth' 
      });
    }
  };

  // Scroll to Spark section handler
  const scrollToSpark = () => {
    scrollToSection("spark-section");
  };

  // Animation effect to cycle through stages
  useEffect(() => {
    // Set initial state
    setDealData(dealStages[0]);
    setVisibleActivities(dealStages[0].activities);

    // Create interval to cycle through stages
    const interval = setInterval(() => {
      setCurrentStage((prevStage) => {
        const nextStage = (prevStage + 1) % dealStages.length;
        setDealData(dealStages[nextStage]);
        setVisibleActivities(dealStages[nextStage].activities);
        return nextStage;
      });
    }, 5000); // Change stage every 5 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  return (
    <Box pt={0} pb={20}>
      <Container maxW="1400px" px={8}>
        {/* Y Combinator Badge */}
        <Flex justify="center" mb={8} pt={8}>
          <Link href="https://www.ycombinator.com/" isExternal _hover={{ textDecoration: 'none' }}>
            <HStack spacing={3} align="center">
              <Text fontSize="md" color={secondaryTextColor} fontWeight="medium">
                Backed by
              </Text>
              <Box 
                bg={ycColor} 
                p={1} 
                borderRadius="lg"
                width="30px" 
                height="30px" 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
              >
                <Text fontSize="md" fontWeight="bold" color="white">
                  Y
                </Text>
              </Box>
              <Text fontSize="md" fontWeight="semibold" color={textColor} ml="-8px">
                Combinator
              </Text>
            </HStack>
          </Link>
        </Flex>

        {/* Main Heading */}
        <Heading
          as="h1"
          fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
          fontWeight="bold"
          textAlign="center"
          mb={6}
          color={textColor}
        >
          Revenue Intelligence, Automated
        </Heading>

        {/* Secondary Text */}
        <Text
          fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
          textAlign="center"
          maxW="800px"
          mx="auto"
          mb={12}
          color={secondaryTextColor}
        >
         Set up in minutes—then let Lysto drive your revenue growth with automatic insights, intelligent answers, and zero busy work.
        </Text>

        {/* Call to Action Buttons */}
        <Stack
          direction={{ base: "column", sm: "row" }}
          spacing={4}
          justify="center"
          mb={16}
        >
          <Button
            bg={primaryButtonBg}
            color={primaryButtonColor}
            size="lg"
            fontSize="md"
            fontWeight="bold"
            rounded="md"
            px={8}
            _hover={{
              bg: "blue.600",
            }}
            onClick={onOpen}
            leftIcon={<MdRocketLaunch />}
          >
            Sign Up
          </Button>
          <Button
            bg={secondaryButtonBg}
            color={secondaryButtonColor}
            size="lg"
            fontSize="md"
            fontWeight="bold"
            rounded="md"
            px={8}
            borderWidth="1px"
            borderColor={secondaryButtonBorder}
            _hover={{
              bg: "gray.100",
            }}
            onClick={scrollToSpark}
          >
            Learn more
          </Button>
        </Stack>

        {/* Browser-like App Preview with Sidebar and DealDetail */}
        <Box
          maxW="1200px"
          mx="auto"
          borderRadius="xl"
          overflow="hidden"
          boxShadow="2xl"
          bg={bgColor}
          border="1px solid"
          borderColor={cardBorderColor}
          position="relative"
          transition="transform 0.3s ease"
          _hover={{
            transform: "scale(1.02)",
          }}
        >
          {/* Browser chrome with address bar */}
          <Box>
            {/* Browser toolbar */}
            <Flex 
              bg={bgColor} 
              p={2} 
              borderBottomWidth="1px" 
              borderColor={cardBorderColor}
              height="40px"
              alignItems="center"
              px={4}
            >
              <HStack spacing={2} mr={4}>
                <Circle size="12px" bg="red.400" />
                <Circle size="12px" bg="yellow.400" />
                <Circle size="12px" bg="green.400" />
              </HStack>
              
              {/* Browser address bar */}
              <Flex 
                flex={1} 
                bg={browserAddressBg} 
                borderRadius="md" 
                px={3} 
                py={1.5}
                fontSize="sm"
                color={secondaryTextColor}
                alignItems="center"
              >
                <Text>https://lysto.ai</Text>
              </Flex>
            </Flex>
          </Box>

          {/* App body with sidebar and content */}
          <Flex flexDirection={{ base: "column", md: "row" }} height={{ base: "auto", md: "650px" }}>
            {/* Sidebar for md and up */}
            <Box display={{ base: "none", md: "block" }}
              width="170px"
              bg={sidebarBg}
              borderRightWidth="1px"
              borderColor={sidebarBorderColor}
              p={0}
              position="relative"
            >
              {/* Lysto Logo and Brand */}
              <Flex
                px={3}
                pt={4}
                pb={0}
                alignItems="center"
                mb={2}
              >
                <Box mr={2} ml={1} width="28px" height="28px" pl={0.5}>
                  <LystoLogo width="100%" height="100%" />
                </Box>
                <Text fontSize="md" fontWeight="bold" color={textColor}>
                  Lysto
                </Text>
              </Flex>

              {/* Navigation Links */}
              <VStack spacing={1} align="stretch" p={2}>
                {navLinks.map((link) => (
                  <Box
                    key={link.name}
                    borderRadius="md"
                    p={2}
                    mb={0.5}
                    bg={link.isActive ? activeLinkBg : "transparent"}
                    color={link.isActive ? activeLinkColor : sidebarTextColor}
                    fontWeight={link.isActive ? "semibold" : "medium"}
                    cursor="pointer"
                    onClick={() => scrollToSection(link.sectionId)}
                    _hover={{
                      bg: link.isActive ? activeLinkBg : "gray.100",
                    }}
                  >
                    <Flex align="center">
                      <Center
                        bg={link.isActive ? "blue.100" : "gray.100"}
                        borderRadius="lg"
                        boxSize="28px"
                        mr={2}
                        color={link.isActive ? activeLinkColor : "gray.500"}
                        pl={0.5}
                      >
                        <Icon as={link.icon} boxSize={3.5} />
                      </Center>
                      <Text fontSize="sm">{link.name}</Text>
                    </Flex>
                  </Box>
                ))}
              </VStack>

              {/* Bottom section with avatar */}
              <Box position={{ base: "static", md: "absolute" }} bottom={{ md: 4 }} width="100%" px={3}>
                <Divider mb={3} borderColor={sidebarBorderColor} />
                <Box
                  borderRadius="md"
                  p={2}
                  color={sidebarTextColor}
                  fontWeight="medium"
                  cursor="pointer"
                  _hover={{
                    bg: "gray.100",
                  }}
                >
                  <Flex align="center">
                    <Avatar
                      size="xs"
                      name="Ricky Roma"
                      bg="blue.500"
                      color="white"
                      mr={2}
                    />
                    <Text fontSize="sm">Ricky Roma</Text>
                  </Flex>
                </Box>
              </Box>
            </Box>

            {/* Main content - Animated Deal Detail */}
            <Box flex={1} bg="gray.50" p={4} overflowY="auto" width={{ base: "100%", md: "auto" }}>
              {/* Deal header with name */}
              <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <Flex alignItems="center">
                  <Center
                    bg="blue.50"
                    borderRadius="lg"
                    boxSize="28px"
                    mr={2}
                    borderWidth="1px"
                    borderColor="blue.100"
                    color="blue.500"
                  >
                    <Icon as={MdTrackChanges} boxSize={3.5} />
                  </Center>
                  <Heading size="md" color={textColor} fontSize="lg">{dealData.name}</Heading>
                </Flex>
                <Badge colorScheme="blue" fontSize="xs" borderRadius="md" px={2} py={1}>
                  <Flex align="center">
                    <Icon as={FaSyncAlt} mr={1} className="rotating-icon" />
                    <Text>REAL-TIME</Text>
                  </Flex>
                </Badge>
              </Flex>
              
              {/* Overview Section - Smaller scale */}
              <Box
                bg={cardBgColor}
                p={4}
                borderRadius="md"
                boxShadow="sm"
                borderWidth="1px"
                borderColor={cardBorderColor}
                mb={3}
                key={`overview-${currentStage}`}
              >
                <Text fontSize="sm" fontWeight="semibold" mb={3}>
                  Overview
                </Text>
                <Flex gap={1.5} flexWrap="wrap">
                  <SlideFade in={true} offsetY="5px">
                    <Box
                      className="mini-tag"
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="blue.50"
                      display="inline-flex"
                      alignItems="center"
                      mr={1.5}
                      mb={1.5}
                    >
                      <Icon as={FaBuilding} color="blue.500" boxSize={2.5} mr={1} />
                      <Text fontSize="xs" fontWeight="medium" color="blue.700">
                        <Text as="span" fontWeight="normal" color="blue.600">Account:</Text> {dealData.accountName}
                      </Text>
                    </Box>
                  </SlideFade>
                  
                  <SlideFade in={true} offsetY="5px" delay={0.1}>
                    <Box
                      className="mini-tag"
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="purple.50"
                      display="inline-flex"
                      alignItems="center"
                      mr={1.5}
                      mb={1.5}
                    >
                      <Icon as={FaDollarSign} color="purple.500" boxSize={2.5} mr={1} />
                      <Text fontSize="xs" fontWeight="medium" color="purple.700">
                        <Text as="span" fontWeight="normal" color="purple.600">Amount:</Text> ${dealData.amount}
                      </Text>
                    </Box>
                  </SlideFade>
                  
                  <SlideFade in={true} offsetY="5px" delay={0.2}>
                    <Box
                      className="mini-tag"
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="orange.50"
                      display="inline-flex"
                      alignItems="center"
                      mr={1.5}
                      mb={1.5}
                    >
                      <Icon as={FaClipboardList} color="orange.500" boxSize={2.5} mr={1} />
                      <Text fontSize="xs" fontWeight="medium" color="orange.700">
                        <Text as="span" fontWeight="normal" color="orange.600">Stage:</Text> {dealData.stage}
                      </Text>
                    </Box>
                  </SlideFade>
                  
                  <SlideFade in={true} offsetY="5px" delay={0.3}>
                    <Box
                      className="mini-tag"
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="green.50"
                      display="inline-flex"
                      alignItems="center"
                      mr={1.5}
                      mb={1.5}
                    >
                      <Icon as={FaPercent} color="green.500" boxSize={2.5} mr={1} />
                      <Text fontSize="xs" fontWeight="medium" color="green.700">
                        <Text as="span" fontWeight="normal" color="green.600">Probability:</Text> {dealData.probability}%
                      </Text>
                    </Box>
                  </SlideFade>
                  
                  <SlideFade in={true} offsetY="5px" delay={0.4}>
                    <Box
                      className="mini-tag"
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="cyan.50"
                      display="inline-flex"
                      alignItems="center"
                      mr={1.5}
                      mb={1.5}
                    >
                      <Icon as={FaClock} color="cyan.500" boxSize={2.5} mr={1} />
                      <Text fontSize="xs" fontWeight="medium" color="cyan.700">
                        <Text as="span" fontWeight="normal" color="cyan.600">Close:</Text> Oct 15, 2023
                      </Text>
                    </Box>
                  </SlideFade>
                  
                  <SlideFade in={true} offsetY="5px" delay={0.5}>
                    <Box
                      className="mini-tag"
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="pink.50"
                      display="inline-flex"
                      alignItems="center"
                      mr={1.5}
                      mb={1.5}
                    >
                      <Icon as={FaTag} color="pink.500" boxSize={2.5} mr={1} />
                      <Text fontSize="xs" fontWeight="medium" color="pink.700">
                        <Text as="span" fontWeight="normal" color="pink.600">Owner:</Text> {dealData.owner}
                      </Text>
                    </Box>
                  </SlideFade>
                </Flex>
              </Box>
              
              {/* Deal Summary Section - Smaller scale */}
              <Box
                bg={cardBgColor}
                p={4}
                borderRadius="md"
                boxShadow="sm"
                borderWidth="1px"
                borderColor={cardBorderColor}
                mb={3}
                key={`summary-${currentStage}`}
              >
                <Flex justifyContent="space-between" alignItems="center" mb={2}>
                  <Text fontSize="sm" fontWeight="semibold">
                    Deal Summary
                  </Text>
                </Flex>
                
                <SlideFade in={true} offsetY="10px">
                  <Box 
                    bg={highlightBg}
                    p={3}
                    borderRadius="md"
                    fontSize="xs"
                    color={secondaryTextColor}
                    mb={2}
                  >
                    <Text>{dealData.summary}</Text>
                  </Box>
                </SlideFade>
                
                <Flex justifyContent="space-between" fontSize="10px" color="gray.500">
                  <Text>Updated {dealData.updatedAt}</Text>
                  <Flex align="center">
                    <Icon as={FaCalendarAlt} size="8px" mr={1} />
                    <Text>{dealData.dateRange}</Text>
                  </Flex>
                </Flex>
              </Box>
              
              {/* Deal Activity Section - Updated layout to match screenshot */}
              <Box
                bg={cardBgColor}
                px={4}
                pt={4}
                pb={4}
                borderRadius="md"
                boxShadow="sm"
                borderWidth="1px"
                borderColor={cardBorderColor}
                key={`activities-${currentStage}`}
              >
                <Text fontSize="sm" fontWeight="semibold" mb={3}>
                  Deal Activities
                </Text>
                
                {/* Activities container without scrolling */}
                <Box>
                  {/* Activity items styled to match Hands-Free Deal Management section */}
                  {visibleActivities.map((activity, index) => (
                    <SlideFade 
                      key={`${activity.id}-${index}`} 
                      in={true} 
                      offsetY="20px"
                      transition={{ enter: { duration: 0.5, delay: index * 0.1 } }}
                    >
                      <Box mb={4} borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" 
                          className={activity.isNew ? "highlight-animation" : ""}>
                        {/* Metadata row - moved to top */}
                        <Flex 
                          justifyContent="space-between" 
                          alignItems="center" 
                          px={4} 
                          py={2.5}
                          borderBottom="1px solid"
                          borderColor="gray.200"
                        >
                          <Flex alignItems="center">
                            <Center
                              bg={`${activity.iconColor}.50`}
                              borderRadius="md"
                              boxSize="24px"
                              mr={2}
                            >
                              <Icon
                                as={activity.icon}
                                color={`${activity.iconColor}.500`}
                                boxSize={3}
                              />
                            </Center>
                            <Flex direction="column">
                              <Flex alignItems="center">
                                <Text fontWeight="medium" color={textColor} fontSize="xs" mr={2}>
                                  {activity.title}
                                </Text>
                                {activity.isNew && (
                                  <Badge colorScheme="green" fontSize="2xs" variant="solid">NEW</Badge>
                                )}
                              </Flex>
                              <Text color={secondaryTextColor} fontSize="10px">
                                {activity.date} • {activity.source}
                              </Text>
                            </Flex>
                          </Flex>
                          
                          <Text textAlign="right" fontSize="xs" fontWeight="medium" color={textColor}>
                            {activity.sender}
                          </Text>
                        </Flex>
                        
                        {/* Content - moved to bottom */}
                        <Box px={4} pt={3} pb={4} fontSize="xs" bg="white">
                          <Text fontSize="xs" color={secondaryTextColor}>
                            {activity.content}
                          </Text>
                        </Box>
                      </Box>
                    </SlideFade>
                  ))}
                </Box>
              </Box>
            </Box>
          </Flex>
        </Box>
      </Container>

      {/* CSS for animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        .rotating-icon {
          animation: rotate 3s linear infinite;
        }
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .mini-tag {
          transition: all 0.2s;
        }
        .mini-tag:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .highlight-animation {
          animation: highlight-fade 2s ease-out;
        }
        @keyframes highlight-fade {
          0% { background-color: rgba(72, 187, 120, 0.1); }
          100% { background-color: transparent; }
        }
      `}} />

      {/* Waitlist Modal */}
      <WaitlistModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default LandingHero; 